import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRetailProduct,
  deleteRetailProduct,
  fetchRetailProducts,
  generateRetailSku,
  updateRetailProduct,
  type CreateRetailProductPayload,
  type ListRetailProductsQuery,
  type UpdateRetailProductPayload,
} from '@/services/api/retailProducts.api';

export function useRetailProducts(query: ListRetailProductsQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-products', query],
    queryFn: () => fetchRetailProducts(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateRetailProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRetailProductPayload) => createRetailProduct(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-stock'] });
    },
  });
}

export function useUpdateRetailProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRetailProductPayload }) =>
      updateRetailProduct(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-stock'] });
    },
  });
}

export function useDeleteRetailProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRetailProduct(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-stock'] });
    },
  });
}

export function useGenerateSku() {
  return useMutation({
    mutationFn: (propertyId: string) => generateRetailSku(propertyId),
  });
}
